import type { SessionProgressResponse, SessionSummaryDto } from "@/features/session-diagnostics/api/session-diagnostics.api";

export type LiveSessionQueueState = {
    queue: SessionSummaryDto[];
    followed: SessionSummaryDto | null;
    followedSince: number | null;
    abandoned: string[];
    failures: number;
    round: number;
    check: { segmentId: string; since: number; round: number } | null;
};

export type LiveSessionProgressOutcome =
    | { kind: "ok"; progress: SessionProgressResponse }
    | { kind: "missing" }
    | { kind: "failed" };

export type LiveSessionQueueAction =
    | { kind: "list"; sessions: SessionSummaryDto[] }
    | { kind: "progress"; segmentId: string; outcome: LiveSessionProgressOutcome };

// A followed session that cannot be read this many times in a row is left, so a broken read never pins the phone.
export const MAX_PROGRESS_FAILURES = 3;

const initial: LiveSessionQueueState = { queue: [], followed: null, followedSince: null, abandoned: [], failures: 0, round: 0, check: null };

const isLive = (session: SessionSummaryDto): boolean => session.coverage === "live" && session.state === "open";

const byOrigin = (left: SessionSummaryDto, right: SessionSummaryDto): number =>
    left.segmentOriginAt !== right.segmentOriginAt ? left.segmentOriginAt - right.segmentOriginAt : left.segmentId.localeCompare(right.segmentId);

// Following starts at the session's last evidence, stamped by the ingest's own clock: a status at or after it belongs
// to the order on screen. No evidence yet (`null`) means no batch is stored, so no earlier status can exist and 0 is exact.
const sinceOf = (session: SessionSummaryDto): number => session.lastEvidenceAt ?? 0;

// `sessions` is the latest list answer only: a session the server no longer lists is not in the queue.
const onList = (state: LiveSessionQueueState, sessions: SessionSummaryDto[]): LiveSessionQueueState => {
    const queue = sessions.filter((session) => isLive(session) && !state.abandoned.includes(session.segmentId)).sort(byOrigin);
    const current = state.followed;
    const fresh = current === null ? undefined : queue.find((session) => session.segmentId === current.segmentId);
    const abandoned = current !== null && fresh === undefined ? [...state.abandoned, current.segmentId] : state.abandoned;
    const followed = fresh === undefined ? (queue[0] ?? null) : fresh;
    const kept = fresh !== undefined;
    const followedSince = followed === null ? null : kept && state.followedSince !== null ? state.followedSince : sinceOf(followed);
    const round = state.round + 1;
    return {
        queue,
        followed,
        followedSince,
        abandoned,
        failures: kept ? state.failures : 0,
        round,
        check: followed === null || followedSince === null ? null : { segmentId: followed.segmentId, since: followedSince, round },
    };
};

// A session is only ever left once: its id stays in `abandoned` for the life of the view, even if it keeps streaming.
const handOver = (state: LiveSessionQueueState, segmentId: string): LiveSessionQueueState => {
    const queue = state.queue.filter((session) => session.segmentId !== segmentId);
    const followed = queue[0] ?? null;
    return { ...state, queue, followed, followedSince: followed === null ? null : sinceOf(followed), failures: 0, abandoned: [...state.abandoned, segmentId] };
};

// The operator's ruling: the order reaching its status screen after the view started following ends the turn.
const onProgress = (state: LiveSessionQueueState, segmentId: string, outcome: LiveSessionProgressOutcome): LiveSessionQueueState => {
    if (state.followed === null || state.followed.segmentId !== segmentId) return state;
    if (outcome.kind === "missing") return handOver(state, segmentId);
    if (outcome.kind === "failed") {
        const failures = state.failures + 1;
        return failures >= MAX_PROGRESS_FAILURES ? handOver(state, segmentId) : { ...state, failures };
    }
    if (outcome.progress.reachedStatusAt !== null || !isLive(outcome.progress.session)) return handOver(state, segmentId);
    return state.failures === 0 ? state : { ...state, failures: 0 };
};

const reduce = (state: LiveSessionQueueState, action: LiveSessionQueueAction): LiveSessionQueueState =>
    action.kind === "list" ? onList(state, action.sessions) : onProgress(state, action.segmentId, action.outcome);

const waiting = (state: LiveSessionQueueState): number => {
    const followed = state.followed;
    return followed === null ? 0 : state.queue.filter((session) => session.segmentId !== followed.segmentId).length;
};

export const liveSessionQueue = { initial, reduce, waiting };

// vb-metrics#38 owner ruling: on a very large screen up to TWO live sessions are followed side by side. Each slot is
// the single-session queue above, with the owner's handover rule applied independently; the slots never follow the
// same session, and a session any slot has left is never followed again by either. With one slot this is exactly the
// single queue.
export type LiveSessionSlotsState = { slots: LiveSessionQueueState[]; latest: SessionSummaryDto[] };
export type LiveSessionSlotsAction = LiveSessionQueueAction | { kind: "slots"; count: number };

const slotsInitial = (count: number): LiveSessionSlotsState => ({ slots: Array.from({ length: Math.max(1, count) }, () => initial), latest: [] });

const abandonedOf = (slots: LiveSessionQueueState[]): Set<string> => new Set(slots.flatMap((slot) => slot.abandoned));

// A slot whose next session is taken by another slot, or was left by another slot, moves on to the next free one.
const withoutTaken = (slot: LiveSessionQueueState, taken: Set<string>): LiveSessionQueueState => {
    const current = slot.followed;
    if (current === null || taken.has(current.segmentId) === false) return slot;
    const queue = slot.queue.filter((session) => taken.has(session.segmentId) === false);
    const followed = queue[0] ?? null;
    return { ...slot, queue, followed, followedSince: followed === null ? null : sinceOf(followed), failures: 0 };
};

const listSlots = (slots: LiveSessionQueueState[], sessions: SessionSummaryDto[]): LiveSessionQueueState[] => {
    const next = [...slots];
    next.forEach((slot, index) => {
        const abandoned = abandonedOf(next);
        const others = new Set(next.flatMap((other, at) => (at === index || other.followed === null ? [] : [other.followed.segmentId])));
        const offered = sessions.filter((session) => others.has(session.segmentId) === false && abandoned.has(session.segmentId) === false);
        next[index] = onList(slot, offered);
    });
    return next;
};

const reduceSlots = (state: LiveSessionSlotsState, action: LiveSessionSlotsAction): LiveSessionSlotsState => {
    if (action.kind === "slots") {
        const count = Math.max(1, action.count);
        if (count === state.slots.length) return state;
        const kept = state.slots.slice(0, count);
        const shared = Array.from(abandonedOf(state.slots));
        while (kept.length < count) kept.push({ ...initial, abandoned: shared });
        return { ...state, slots: state.latest.length === 0 ? kept : listSlots(kept, state.latest) };
    }
    if (action.kind === "list") return { slots: listSlots(state.slots, action.sessions), latest: action.sessions };
    const index = state.slots.findIndex((slot) => slot.followed !== null && slot.followed.segmentId === action.segmentId);
    if (index === -1) return state;
    const slots = [...state.slots];
    const updated = onProgress(slots[index] ?? initial, action.segmentId, action.outcome);
    if (updated === slots[index]) return state;
    slots[index] = updated;
    const taken = new Set([
        ...Array.from(abandonedOf(slots.filter((_slot, at) => at !== index))),
        ...slots.flatMap((other, at) => (at === index || other.followed === null ? [] : [other.followed.segmentId])),
    ]);
    slots[index] = withoutTaken(updated, taken);
    return { ...state, slots };
};

// Everything live the latest answer lists beyond the followed sessions, and never one a slot has left.
const slotsWaiting = (state: LiveSessionSlotsState): number => {
    const followed = new Set(state.slots.flatMap((slot) => (slot.followed === null ? [] : [slot.followed.segmentId])));
    if (followed.size === 0) return 0;
    const abandoned = abandonedOf(state.slots);
    return state.latest.filter((session) => isLive(session) && followed.has(session.segmentId) === false && abandoned.has(session.segmentId) === false).length;
};

export const liveSessionSlots = { initial: slotsInitial, reduce: reduceSlots, waiting: slotsWaiting };
